import { useState, useEffect } from "react";
import api from "../../services/api";
import DataTable from "react-data-table-component";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencil } from "@fortawesome/free-solid-svg-icons";
import { faSquarePlus, faSquareMinus } from "@fortawesome/free-solid-svg-icons";
import Swal from "sweetalert2";
import EditModal from "../ui/EditModal.js";
import { normalizar } from "../../common/stringNormalizer.js";

const ZonasSanitarias = () => {
  const [filtroEstado, setFiltroEstado] = useState("activas");
  const [Nombre, setNombre] = useState("");
  const [Descripcion, setDescripcion] = useState("");
  const [IdZonaGeografica, setIdZonaGeografica] = useState("");
  const [localidades, setLocalidades] = useState([]);
  const [Zonas, setZonas] = useState([]);
  const [mostrarDescripcion, setMostrarDescripcion] = useState({});
  const [selectedZona, setSelectedZona] = useState(null);

  useEffect(() => {
    getZonas();
    getLocalidades();
  }, []);

  const localidadSeleccionada = localidades.find(
    (localidad) => Number(localidad.IdZonaGeografica) === Number(IdZonaGeografica)
  );

  const buildZonaGeografica = (zona) => {
    if (!zona) return "";
    if (normalizar(zona.TipoZonaGeografica || "") === "provincia") {
      return [zona.Pais, zona.Provincia || zona.Nombre]
        .filter(Boolean)
        .join(" --> ");
    }
    return [zona.Pais, zona.Provincia, zona.Localidad || zona.Nombre]
      .filter(Boolean)
      .join(" --> ");
  };

  const buildZonaGeograficaDetalle = (zona) => {
    if (!zona) return "";
    const ubicacion = buildZonaGeografica(zona);
    const domicilio = zona.DireccionCompleta || [zona.Calle, zona.Numero].filter(Boolean).join(" ");
    return [ubicacion, zona.Barrio, domicilio].filter(Boolean).join(" - ");
  };

  const editFields = [
    { name: "Nombre", label: "Nombre Zona Sanitaria", type: "text", required: true },
    { name: "Descripcion", label: "Descripción Zona", type: "text", required: false }
  ];

  const getZonas = async () => {
    try {
      const response = await api.get(`/zonas-sanitarias`);
      setZonas(response.data);
    } catch (error) {
      console.error("Error al obtener zonas:", error);
    }
  };

  const getLocalidades = async () => {
    try {
      const response = await api.get(
        `/zonas-geograficas/localidades?incluirProvincias=1`
      );
      setLocalidades(response.data);
    } catch (error) {
      console.error("Error al obtener localidades:", error);
    }
  };


  const createZona = async (e) => {
    e.preventDefault();

    const existe = Zonas.some(
      (zona) => normalizar(zona.Nombre) === normalizar(Nombre)
    );

    if (existe) {
      Swal.fire({
        icon: "warning",
        title: "Duplicado",
        text: "Ya existe una zona sanitaria con ese nombre.",
        confirmButtonColor: "#3085d6"
      });
      return;
    }

    try {
      await api.post(`/zonas-sanitarias`, {
        Nombre,
        Descripcion,
        IdZonaGeografica: IdZonaGeografica || null
      });
      getZonas();
      setNombre("");
      setDescripcion("");
      setIdZonaGeografica("");
      Swal.fire({
        icon: "success",
        title: "Guardado",
        text: "La zona sanitaria fue guardada exitosamente.",
        timer: 1500,
        showConfirmButton: false
      });
    } catch (error) {
      console.error(error);
      Swal.fire({
        icon: "error",
        title: "Error al guardar",
        text: "Ocurrió un problema al guardar la zona sanitaria.",
      });
    }
  };

  const handleEditZona = async (values) => {
    try {
      await api.put(
        `/zonas-sanitarias/${values.IdZonaSanitaria}`,
        { Nombre: values.Nombre, Descripcion: values.Descripcion }
      );
      Swal.fire({
        icon: "success",
        title: "Zona actualizada",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (error) {
      console.error("Error al actualizar:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Ocurrió un error al actualizar la zona sanitaria.",
      });
    }
    setSelectedZona(null);
    getZonas();
  };

  const cambiarEstado = async (IdZonaSanitaria) => {
    try {
      const zonaActual = Zonas.find((z) => z.IdZonaSanitaria === IdZonaSanitaria);
      const nuevoEstado = zonaActual.Estado === 1 ? "desactivada" : "activada";

     await api.patch(`/zonas-sanitarias/${IdZonaSanitaria}/toggle`);
      await getZonas();

      Swal.fire({
        icon: "success",
        title: `Zona ${nuevoEstado}`,
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error(error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Ocurrió un error al cambiar el estado de la zona sanitaria.",
      });
    }
  };

  const filtrarZonas = () => {
    if (filtroEstado === "activas") return Zonas.filter(z => z.Estado === 1);
    if (filtroEstado === "inactivas") return Zonas.filter(z => z.Estado === 0);
    return Zonas;
  };

  const columnas = [
    {
      name: "Nombre",
      selector: (row) => row.Nombre,
      sortable: true,
      width: "15vw",
    },
    {
      name: "Descripción",
      cell: (row) => {
        const toggleDescripcion = () => {
          setMostrarDescripcion({
            ...mostrarDescripcion,
            [row.IdZonaSanitaria]: !mostrarDescripcion[row.IdZonaSanitaria],
          });
        };

        const descripcion = row.Descripcion || "Sin descripción";
        const mostrarBoton = row.Descripcion && row.Descripcion.length > 40;

        const estiloCelda = {
          display: "flex",
          alignItems: "center",
          width: "30vw",
          maxHeight: mostrarDescripcion[row.IdZonaSanitaria] ? "none" : "40vh",
          overflow: mostrarDescripcion[row.IdZonaSanitaria] ? "visible" : "hidden",
        };

        const estiloContenido = {
          flex: 1,
          maxHeight: "40vh",
          textOverflow: mostrarDescripcion[row.IdZonaSanitaria] ? "clip" : "ellipsis",
          whiteSpace: mostrarDescripcion[row.IdZonaSanitaria] ? "normal" : "nowrap",
        };

        return (
          <div style={estiloCelda}>
            <div style={estiloContenido}>
              {!mostrarDescripcion[row.IdZonaSanitaria]
                ? descripcion.length > 40
                  ? descripcion.substring(0, 40) + "..."
                  : descripcion
                : descripcion}
            </div>
            {mostrarBoton && (
              <button
                className="btn"
                onClick={(e) => { toggleDescripcion(); e.preventDefault(); }}
              >
                {mostrarDescripcion[row.IdZonaSanitaria] ? "Ver Menos" : "Ver Más"}
              </button>
            )}
          </div>
        );
      },
      sortable: true,
      width: "30vw",
    },
    {
      name: "Zona geografica",
      selector: (row) => buildZonaGeografica(row) || "Sin zona geografica",
      sortable: true,
      width: "28vw",
      wrap: true,
    },
    {
      cell: (row) => (
        <button
          className="button is-small is-info"
          title="Editar"
          onClick={() => setSelectedZona(row)}
        >
          <FontAwesomeIcon icon={faPencil} />
        </button>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      compact: true,
    },
    {
      cell: (row) =>
        row.Estado === 1 ? (
          <FontAwesomeIcon
            icon={faSquarePlus}
            style={{ color: "#37ff00", cursor: "pointer" }}
            size="2xl"
            onClick={() => cambiarEstado(row.IdZonaSanitaria)}
          />
        ) : (
          <FontAwesomeIcon
            icon={faSquareMinus}
            style={{ color: "#ff0000", cursor: "pointer" }}
            size="2xl"
            onClick={() => cambiarEstado(row.IdZonaSanitaria)}
          />
        ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      compact: true,
    },
  ];

  const paginationOptionsES = {
    rowsPerPageText: 'Filas por página',
    rangeSeparatorText: 'de',
    selectAllRowsItem: true,
    selectAllRowsItemText: 'Todos',
  };

  return (
    <div className="columns mt-5 is-centered">
      <div>
        <div className="notification is-primary">
          <strong>Ingresa el nombre de la zona sanitaria.</strong>
        </div>
        <form onSubmit={createZona}>
          <div className="field">
            <div className="container">
              <label className="label required">Nombre Zona</label>
              <div className="control">
                <input
                  type="text"
                  className="input"
                  value={Nombre}
                  onChange={(e) => setNombre(e.target.value)}
                  placeholder="Nombre"
                  required
                />
              </div>
            </div>
          </div>

          <div className="field">
            <div className="container">
              <label className="label">Descripción Zona</label>
              <div className="control">
                <input
                  type="text"
                  className="input"
                  value={Descripcion}
                  onChange={(e) => setDescripcion(e.target.value)}
                  placeholder="Descripción"
                />
              </div>
            </div>
          </div>

          <div className="field">
            <div className="container">
              <label className="label">Zona geografica</label>
              <div className="control">
                <div className="select is-fullwidth">
                  <select
                    value={IdZonaGeografica}
                    onChange={(e) => setIdZonaGeografica(e.target.value)}
                  >
                    <option value="">Seleccionar zona geografica</option>
                    {localidades.map((localidad) => (
                      <option
                        key={localidad.IdZonaGeografica}
                        value={localidad.IdZonaGeografica}
                      >
                        {localidad.TipoZonaGeografica} — {buildZonaGeograficaDetalle(localidad)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {buildZonaGeografica(localidadSeleccionada) && (
                <p
                  className="help"
                  style={{ color: "#2563eb", fontWeight: 700, marginTop: "6px" }}
                >
                  {buildZonaGeografica(localidadSeleccionada)}
                </p>
              )}
            </div>
          </div>

          <div className="field mt-3">
            <button type="submit" className="button is-success">Guardar</button>
          </div>
        </form>

        <br />

        <div style={{ border: "1px solid gray", borderRadius: "8px" }}>
          <div style={{ display: 'flex', gap: '10px', marginBottom: '1rem' }}>
            {["todas", "activas", "inactivas"].map((estado) => (
              <button
                key={estado}
                onClick={() => setFiltroEstado(estado)}
                type="button"
                style={{
                  padding: "6px 16px",
                  border: "none",
                  borderRadius: "20px",
                  backgroundColor: filtroEstado === estado ? "#d1f5d3" : "#f0f2f5",
                  color: filtroEstado === estado ? "#075e54" : "#333",
                  fontWeight: filtroEstado === estado ? "bold" : "normal",
                  boxShadow: filtroEstado === estado ? "inset 0 0 0 1px #1ebea5" : "inset 0 0 0 1px #ccc",
                  cursor: "pointer",
                  transition: "all 0.2s ease-in-out",
                  fontSize: "0.9rem",
                }}
              >
                {estado.charAt(0).toUpperCase() + estado.slice(1)}
              </button>
            ))}
          </div>

          <DataTable
            pagination
            paginationComponentOptions={paginationOptionsES}
            columns={columnas}
            data={filtrarZonas()}
            dense={false}
            responsive={false}
          />
        </div>

        {selectedZona && (
          <EditModal
            fields={editFields}
            initialValues={selectedZona}
            onSubmit={handleEditZona}
            title="Editar Zona Sanitaria"
            onClose={() => setSelectedZona(null)}
          />
        )}

      </div>
    </div>
  );
};

export default ZonasSanitarias;
